import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  globalKnowledgeService,
  GlobalCategory,
  GlobalDocument,
} from '@/services/api/global-knowledge';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Search,
  FileText,
  ExternalLink,
  Shield,
  ChevronRight,
  Calendar,
  BookOpen,
  X,
  Loader2,
  FolderOpen,
} from 'lucide-react';

const CATEGORY_COLORS: Record<string, string> = {
  'asic-insolvency': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  'asic-director-duties': 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  'asic-regulatory-guides': 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
  'asic-company-compliance': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  'asic-financial-services': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  'asic-smsf': 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  'asic-consumer-protection': 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300',
  'asic-news-updates': 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300',
};

const CATEGORY_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  'asic-insolvency': Shield,
  'asic-director-duties': FileText,
  'asic-regulatory-guides': BookOpen,
  'asic-company-compliance': FileText,
  'asic-financial-services': FileText,
  'asic-smsf': Shield,
  'asic-consumer-protection': Shield,
  'asic-news-updates': Calendar,
};

export function AsicKnowledgeBasePage() {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDocument, setSelectedDocument] = useState<GlobalDocument | null>(null);
  const [contentDialogOpen, setContentDialogOpen] = useState(false);

  // Fetch all categories, filter to ASIC domain
  const { data: categoriesData, isLoading: categoriesLoading } = useQuery({
    queryKey: ['asic-kb-categories'],
    queryFn: async () => {
      const result = await globalKnowledgeService.listCategories();
      return (result.categories || []).filter((c: any) => c.domainSlug === 'asic');
    },
  });

  const categories = categoriesData || [];

  // Fetch documents for selected category
  const { data: documentsData, isLoading: documentsLoading } = useQuery({
    queryKey: ['asic-kb-docs', selectedCategory],
    queryFn: () => globalKnowledgeService.listDocuments({ categoryId: selectedCategory!, limit: 100 }),
    enabled: !!selectedCategory,
  });

  const documents = documentsData?.documents || [];

  // Search across all ASIC docs
  const { data: searchData, isLoading: searchLoading } = useQuery({
    queryKey: ['asic-kb-search', searchQuery],
    queryFn: () => globalKnowledgeService.listDocuments({ search: searchQuery, limit: 50 }),
    enabled: searchQuery.length >= 3,
  });

  // Filter search results to only ASIC categories
  const asicCategorySlugs = useMemo(
    () => new Set(categories.map((c: GlobalCategory) => c.slug)),
    [categories]
  );

  const searchResults = useMemo(() => {
    if (!searchData?.documents) return [];
    return searchData.documents.filter(
      (d: GlobalDocument) => d.categorySlug && asicCategorySlugs.has(d.categorySlug)
    );
  }, [searchData, asicCategorySlugs]);

  const isSearchMode = searchQuery.length >= 3;
  const displayDocs = isSearchMode ? searchResults : documents;

  // Load document content
  const { data: docContent, isLoading: contentLoading } = useQuery({
    queryKey: ['asic-kb-content', selectedDocument?.id],
    queryFn: () => globalKnowledgeService.getDocumentContent(selectedDocument!.id),
    enabled: !!selectedDocument?.id && contentDialogOpen,
  });

  const selectedCategoryObj = categories.find((c: GlobalCategory) => c.id === selectedCategory);

  const totalDocCount = categories.reduce((sum: number, c: GlobalCategory) => sum + (c.documentCount || 0), 0);

  return (
    <div className="flex flex-col h-full">
      {/* Page Header */}
      <div className="border-b bg-background px-6 py-4">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
            <Shield className="h-6 w-6 text-blue-700 dark:text-blue-300" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">ASIC Knowledge Base</h1>
            <p className="text-sm text-muted-foreground">
              ASIC regulatory guides, insolvency, director duties, and compliance resources
            </p>
          </div>
        </div>
        {/* Search */}
        <div className="relative mt-3 max-w-xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search ASIC documents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 pr-9"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2"
            >
              <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
            </button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left Sidebar - Categories */}
        <div className="w-72 border-r bg-muted/30 flex flex-col">
          <div className="p-3 border-b">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
              Categories
            </h3>
            <p className="text-xs text-muted-foreground mt-1">
              {categories.length} categories, {totalDocCount} documents
            </p>
          </div>
          <ScrollArea className="flex-1">
            <div className="p-2 space-y-1">
              {categoriesLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full rounded-lg" />
                ))
              ) : categories.length === 0 ? (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  <FolderOpen className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No ASIC categories found.</p>
                  <p className="text-xs mt-1">Run the ASIC KB import first.</p>
                </div>
              ) : (
                categories.map((cat: GlobalCategory) => {
                  const Icon = CATEGORY_ICONS[cat.slug] || FileText;
                  const isSelected = cat.id === selectedCategory;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => {
                        setSelectedCategory(cat.id);
                        setSearchQuery('');
                      }}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                        isSelected
                          ? 'bg-primary/10 text-primary border border-primary/20'
                          : 'hover:bg-muted text-foreground'
                      }`}
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{cat.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {cat.documentCount || 0} docs
                        </p>
                      </div>
                      {isSelected && <ChevronRight className="h-4 w-4 shrink-0" />}
                    </button>
                  );
                })
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Right Content - Documents */}
        <div className="flex-1 overflow-auto">
          {isSearchMode ? (
            <div className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Search className="h-4 w-4 text-muted-foreground" />
                <h2 className="text-lg font-semibold">
                  Search Results for "{searchQuery}"
                </h2>
                {searchLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              </div>
              {searchLoading ? (
                <div className="space-y-3">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} className="h-24 w-full rounded-lg" />
                  ))}
                </div>
              ) : searchResults.length === 0 ? (
                <p className="text-muted-foreground">No ASIC documents match your search.</p>
              ) : (
                <div className="space-y-3">
                  {searchResults.map((doc: GlobalDocument) => (
                    <DocumentCard
                      key={doc.id}
                      doc={doc}
                      onViewContent={() => {
                        setSelectedDocument(doc);
                        setContentDialogOpen(true);
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : !selectedCategory ? (
            <div className="flex items-center justify-center h-full text-muted-foreground">
              <div className="text-center">
                <Shield className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p className="text-lg font-medium">Select a category</p>
                <p className="text-sm mt-1">Choose an ASIC category from the sidebar to browse documents</p>
              </div>
            </div>
          ) : (
            <div className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <h2 className="text-lg font-semibold">
                  {selectedCategoryObj?.name || 'Documents'}
                </h2>
                {selectedCategoryObj?.description && (
                  <span className="text-sm text-muted-foreground">
                    - {selectedCategoryObj.description}
                  </span>
                )}
                {documentsLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              </div>
              {documentsLoading ? (
                <div className="space-y-3">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} className="h-24 w-full rounded-lg" />
                  ))}
                </div>
              ) : documents.length === 0 ? (
                <p className="text-muted-foreground">No documents in this category yet.</p>
              ) : (
                <div className="space-y-3">
                  {documents.map((doc: GlobalDocument) => (
                    <DocumentCard
                      key={doc.id}
                      doc={doc}
                      onViewContent={() => {
                        setSelectedDocument(doc);
                        setContentDialogOpen(true);
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Content Dialog */}
      <Dialog open={contentDialogOpen} onOpenChange={setContentDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {selectedDocument?.originalFilename || selectedDocument?.filename}
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh] mt-2">
            {contentLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin mr-2" />
                <span>Loading content...</span>
              </div>
            ) : (
              <div className="prose dark:prose-invert max-w-none text-sm whitespace-pre-wrap p-4">
                {docContent?.contentText || selectedDocument?.contentText || 'No content available.'}
              </div>
            )}
          </ScrollArea>
          {selectedDocument?.sourceUrl && (
            <div className="mt-2 pt-2 border-t">
              <a
                href={selectedDocument.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                View on ASIC website
              </a>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Document card component
function DocumentCard({
  doc,
  onViewContent,
}: {
  doc: GlobalDocument;
  onViewContent: () => void;
}) {
  const categoryColor = CATEGORY_COLORS[doc.categorySlug || ''] || 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300';

  return (
    <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={onViewContent}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-sm truncate">
              {doc.originalFilename || doc.filename}
            </h3>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              {doc.categoryName && (
                <Badge variant="secondary" className={`text-xs ${categoryColor}`}>
                  {doc.categoryName}
                </Badge>
              )}
              {doc.wordCount && (
                <span className="text-xs text-muted-foreground">
                  {doc.wordCount.toLocaleString()} words
                </span>
              )}
              <span className="text-xs text-muted-foreground">
                Updated {new Date(doc.updatedAt).toLocaleDateString('en-AU')}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {doc.sourceUrl && (
              <a
                href={doc.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-muted-foreground hover:text-blue-600"
                title="Open on ASIC website"
              >
                <ExternalLink className="h-4 w-4" />
              </a>
            )}
            <Button variant="ghost" size="sm" onClick={onViewContent}>
              View
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default AsicKnowledgeBasePage;
